import { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  IconButton,
  SvgIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { tallyTokens } from "../../theme.tsx";

import { useTypedSelector } from "../../hooks.tsx";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import {
  getIsConversationWorking,
  getWorkingConversationCount,
  MAX_CONCURRENT_QUESTIONS,
} from "../../store/conversation.tsx";
import TallyButton from "../common/TallyButton.tsx";

interface ExampleItemsProps {
  children?: React.ReactNode;
  isCircleView?: boolean;
}

interface ViewProps {
  exampleItems: string[];
  disabled: boolean;
  handleOnClick: React.MouseEventHandler<HTMLButtonElement>;
  isCircleView?: boolean;
}

const ITEM_HEIGHT = 26;
const ITEM_WIDTH = 140;

const MobileView: React.FC<ViewProps> = ({
  exampleItems,
  disabled,
  handleOnClick,
  isCircleView,
}) => {
  const [centerIndex, setCenterIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loopedItems = [
    ...exampleItems.slice(-5),
    ...exampleItems,
    ...exampleItems.slice(0, 5),
  ];

  useEffect(() => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const total = exampleItems.length;

    if (isCircleView) {
      // vertical: scrollTop to middle
      container.scrollTop = total * ITEM_HEIGHT;
    } else {
      // horizontal: scrollLeft to middle
      container.scrollLeft = total * ITEM_WIDTH;
    }
  }, [exampleItems.length, isCircleView]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const idx = Math.round(container.scrollTop / ITEM_HEIGHT);
    setCenterIndex(idx);

    const total = exampleItems.length;
    const maxScroll = container.scrollHeight - container.clientHeight;

    // if we scrolled into the clones → jump back to the real list
    if (container.scrollTop <= ITEM_HEIGHT) {
      // at the top clones → jump to equivalent position at bottom
      container.scrollTop += total * ITEM_HEIGHT;
    } else if (container.scrollTop >= maxScroll - ITEM_HEIGHT) {
      // at the bottom clones → jump to equivalent position at top
      container.scrollTop -= total * ITEM_HEIGHT;
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%", // fills the bottom 1/3
        display: "flex",
        justifyContent: "center", // center scroll list vertically
        alignItems: "flex-start",
      }}
    >
      {/* Scrollable list */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          overflowY: isCircleView ? "auto" : "hidden",
          overflowX: isCircleView ? "hidden" : "auto",
          height: isCircleView ? `${ITEM_HEIGHT * 3}px` : `${ITEM_HEIGHT}px`,
          width: isCircleView ? "100%" : "auto",
          scrollbarWidth: "none",
          display: !isCircleView ? "flex" : "block",
          flexDirection: !isCircleView ? "row" : undefined,
          alignItems: !isCircleView ? "center" : undefined,
          gap: !isCircleView ? "12px" : undefined,
          scrollSnapType: !isCircleView ? "x mandatory" : undefined,
          "&::-webkit-scrollbar": { display: "none" },
          // mask for horizontal only
          WebkitMaskImage: !isCircleView
            ? "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)"
            : undefined,
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
        }}
      >
        {loopedItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              scrollSnapAlign: "center",
              height: isCircleView ? `${ITEM_HEIGHT}px` : "100%",
              width: isCircleView ? "100%" : "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
              opacity: !isCircleView ? 1 : idx === centerIndex ? 1 : 0.4,
              transform: !isCircleView
                ? "scale(1)"
                : idx === centerIndex
                  ? "scale(1)"
                  : "scale(0.9)",
              transition: "opacity 0.3s, transform 0.3s",
            }}
          >
            <TallyButton
              onClick={handleOnClick}
              disabled={disabled}
              sx={{
                borderColor: !isCircleView
                  ? "currentColor"
                  : idx === centerIndex
                    ? "currentColor"
                    : "rgba(0,0,0,0.2)",
                transition: "border-color 0.3s, border-width 0.3s",
              }}
            >
              {item}
            </TallyButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const DesktopView: React.FC<ViewProps> = ({
  exampleItems,
  disabled,
  handleOnClick,
  isCircleView,
}) => {
  // In the circle the examples are the whole bottom third, so they wrap into a
  // centred block that fills the space. Under the composer they are a single
  // scrollable row, faded at the trailing edge.
  if (isCircleView) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          width: "100%",
          justifyContent: "center",
          alignContent: "flex-start",
        }}
      >
        {exampleItems.map((example: string) => (
          <TallyButton
            key={example}
            onClick={handleOnClick}
            disabled={disabled}
          >
            {example}
          </TallyButton>
        ))}
      </Box>
    );
  }

  return (
    <ChipRow>
      {exampleItems.map((example: string) => (
        <TallyButton
          key={example}
          onClick={handleOnClick}
          disabled={disabled}
          sx={{ flexShrink: 0 }}
        >
          {example}
        </TallyButton>
      ))}
    </ChipRow>
  );
};

const FADE_PX = 48;

const ChevronIcon: React.FC<{ direction: "left" | "right" }> = ({
  direction,
}) => (
  <SvgIcon
    viewBox="0 0 24 24"
    sx={{
      fontSize: 14,
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
  >
    <path d={direction === "left" ? "m15 6-6 6 6 6" : "m9 6 6 6-6 6"} />
  </SvgIcon>
);

/**
 * A single row of example chips under the composer. It scrolls sideways, which
 * a trackpad can do but a mouse wheel cannot, so the wheel is turned into
 * horizontal movement and an arrow appears on each side that has more chips.
 */
const ChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateEdges = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    setCanScrollLeft(row.scrollLeft > 1);
    setCanScrollRight(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    updateEdges();

    // A mouse wheel only scrolls vertically; move the row instead while it
    // has somewhere to go, and let the page have the wheel otherwise.
    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = row.scrollWidth - row.clientWidth;
      if (max <= 0) return;
      const atStart = row.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = row.scrollLeft >= max && event.deltaY > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      row.scrollLeft += event.deltaY;
    };

    const resizeObserver = new ResizeObserver(updateEdges);
    resizeObserver.observe(row);
    row.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      resizeObserver.disconnect();
      row.removeEventListener("wheel", handleWheel);
    };
  }, [updateEdges]);

  const scrollByPage = (direction: -1 | 1) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({
      left: direction * row.clientWidth * 0.7,
      behavior: "smooth",
    });
  };

  const fadeMask = `linear-gradient(to right, ${
    canScrollLeft ? "transparent 0" : "#000 0"
  }, #000 ${canScrollLeft ? FADE_PX : 0}px, #000 calc(100% - ${
    canScrollRight ? FADE_PX : 0
  }px), ${canScrollRight ? "transparent 100%" : "#000 100%"})`;

  const arrowSx = (side: "left" | "right") => ({
    position: "absolute",
    top: "50%",
    [side]: 0,
    transform: "translateY(-50%)",
    zIndex: 1,
    width: "26px",
    height: "26px",
    padding: 0,
    backgroundColor: tallyTokens.surface,
    border: `1px solid ${tallyTokens.line}`,
    boxShadow: tallyTokens.shadow.sm,
    color: tallyTokens.ink2,
    "&:hover": {
      backgroundColor: tallyTokens.surface,
      borderColor: tallyTokens.lineStrong,
      color: tallyTokens.ink,
    },
  });

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {canScrollLeft && (
        <IconButton
          aria-label={i18n._(
            msg({
              context:
                "Aria label for the button that scrolls the example questions back",
              message: "Previous examples",
            }),
          )}
          onClick={() => scrollByPage(-1)}
          sx={arrowSx("left")}
        >
          <ChevronIcon direction="left" />
        </IconButton>
      )}
      <Box
        ref={rowRef}
        onScroll={updateEdges}
        sx={{
          display: "flex",
          gap: "7px",
          overflowX: "auto",
          width: "100%",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          WebkitMaskImage: fadeMask,
          maskImage: fadeMask,
        }}
      >
        {children}
      </Box>
      {canScrollRight && (
        <IconButton
          aria-label={i18n._(
            msg({
              context:
                "Aria label for the button that scrolls to more example questions",
              message: "More examples",
            }),
          )}
          onClick={() => scrollByPage(1)}
          sx={arrowSx("right")}
        >
          <ChevronIcon direction="right" />
        </IconButton>
      )}
    </Box>
  );
};

const ExampleItems: React.FC<ExampleItemsProps> = ({ isCircleView }) => {
  const dispatch = useDispatch();
  const exampleItems = useTypedSelector(
    (state) => state.exampleState.exampleItems,
  );
  // Gate on the selected conversation working (matching the input), or on the
  // concurrency cap being reached (matching the input's submit guard).
  const disableExamples = useTypedSelector((state) => {
    const selectedConversation = state.conversationState.selectedConversation;
    return (
      getIsConversationWorking(state, selectedConversation) ||
      getWorkingConversationCount(state) >= MAX_CONCURRENT_QUESTIONS
    );
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Handle click on example button
  const handleOnClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    let searchText = event.currentTarget.textContent;
    dispatch(searchFieldActions.setTextAndSubmit(searchText));
  };

  if (isMobile) {
    return (
      <MobileView
        exampleItems={exampleItems}
        disabled={disableExamples}
        handleOnClick={handleOnClick}
        isCircleView={isCircleView}
      />
    );
  } else {
    return (
      <DesktopView
        exampleItems={exampleItems}
        disabled={disableExamples}
        handleOnClick={handleOnClick}
        isCircleView={isCircleView}
      />
    );
  }
};

export default ExampleItems;
